/**
 * useAuth hook for the Outlook Add-in.
 *
 * Fetches the current user's identity and permissions from GET /api/auth/me
 * on mount. Provides role-based permission checks and handles the
 * "account disabled" case by logging out immediately.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../auth/apiClient";
import { initSession, signOut } from "../auth/authService";
export function useAuth() {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);
    const [permissions, setPermissions] = useState(new Set());
    const fetchMe = useCallback(async () => {
        setIsLoading(true);
        try {
            // Ensure we have a session before calling /me. In DEV_AUTO_LOGIN mode
            // this hits POST /api/auth/dev-login; in production it acquires an
            // Azure AD token and exchanges it for a session cookie. Calling it
            // proactively avoids the 401→retry race that can silently fail when
            // MSAL/Office SSO isn't configured.
            await initSession();
            const response = await apiFetch("/api/auth/me", { method: "GET" });
            if (!response.ok) {
                setUser(null);
                setPermissions(new Set());
                return;
            }
            const data = await response.json();
            handleUserData(data);
        }
        catch (err) {
            console.error("[useAuth] Failed to fetch user info:", err);
            setUser(null);
            setPermissions(new Set());
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const handleUserData = useCallback((data) => {
        if (data.is_active === false) {
            // Account disabled — log out immediately and show message
            setIsDisabled(true);
            setUser(null);
            setPermissions(new Set());
            signOut().catch(() => { });
            return;
        }
        setUser(data);
        setPermissions(new Set(data.permissions || []));
    }, []);
    useEffect(() => {
        fetchMe();
    }, [fetchMe]);
    const hasPermission = useCallback((perm) => permissions.has(perm), [permissions]);
    const logout = useCallback(async () => {
        try {
            await apiFetch("/api/auth/logout", { method: "POST" });
        }
        catch (_a) {
            /* best effort */
        }
        await signOut();
        setUser(null);
        setPermissions(new Set());
    }, []);
    return {
        user,
        isLoading,
        isDisabled,
        permissions,
        hasPermission,
        logout,
        refresh: fetchMe,
    };
}
